import { useCallback, useContext, useEffect, useState } from "react";

import styled, { keyframes } from "styled-components/macro";
import { uid } from "utils";

import { ScreenshotContext } from "../../screenshot/contexts/ScreenshotContext";

export enum ToastType {
  Info = "info",
  Warning = "warning",
  Error = "error",
  Success = "success"
}

export interface ChartToastMessage {
  _id: string; // internal unique id
  toastId?: string; // external id for controlling duplicates/dismissal
  message: string;
  type: ToastType;
  duration?: number;
}

type ToastEventDetail =
  | { action: "show"; payload: Omit<ChartToastMessage, "_id"> }
  | { action: "dismiss"; toastId: string }
  | { action: "dismissAll" };

const getEventName = (chartId: string) => `chart-toast-${chartId}`;

export const showChartToast = (
  chartId: string,
  message: string,
  type: ToastType = ToastType.Info,
  options?: { duration?: number; toastId?: string }
) => {
  const event = new CustomEvent<ToastEventDetail>(getEventName(chartId), {
    detail: {
      action: "show",
      payload: {
        message,
        type,
        duration: options?.duration ?? 5000,
        toastId: options?.toastId
      }
    }
  });
  window.dispatchEvent(event);
};

export const dismissChartToast = (chartId: string, toastId: string) => {
  const event = new CustomEvent<ToastEventDetail>(getEventName(chartId), {
    detail: {
      action: "dismiss",
      toastId
    }
  });
  window.dispatchEvent(event);
};

export const dismissAllChartToasts = (chartId: string) => {
  const event = new CustomEvent<ToastEventDetail>(getEventName(chartId), {
    detail: {
      action: "dismissAll"
    }
  });
  window.dispatchEvent(event);
};

export const ChartToast = ({ chartId }: { chartId: string }) => {
  const [toasts, setToasts] = useState<ChartToastMessage[]>([]);

  // Use context directly to safely handle when provider is not available (e.g., in tests)
  const screenshotContext = useContext(ScreenshotContext);
  const screenshotActive = !!screenshotContext?.widget;

  useEffect(() => {
    const handleEvent = (e: Event) => {
      const customEvent = e as CustomEvent<ToastEventDetail>;
      const detail = customEvent.detail;

      if (detail.action === "show") {
        const { toastId } = detail.payload;
        setToasts((prev) => {
          let next = prev;
          if (toastId) {
            // Remove existing toast with same toastId to mimic update/replace
            next = prev.filter((t) => t.toastId !== toastId);
          }
          // Limit to 2 toasts as per original Chart.tsx limit={2}
          if (next.length >= 2) {
            next = next.slice(1);
          }
          return [...next, { ...detail.payload, _id: uid() }];
        });
      } else if (detail.action === "dismiss") {
        setToasts((prev) => prev.filter((t) => t.toastId !== detail.toastId));
      } else if (detail.action === "dismissAll") {
        setToasts([]);
      }
    };

    const eventName = getEventName(chartId);
    window.addEventListener(eventName, handleEvent);

    return () => {
      window.removeEventListener(eventName, handleEvent);
    };
  }, [chartId]);

  // Dismiss all toasts when entering screenshot mode
  useEffect(() => {
    if (screenshotActive) {
      setToasts([]);
    }
  }, [screenshotActive]);

  const removeToast = useCallback((_id: string) => {
    setToasts((prev) => prev.filter((t) => t._id !== _id));
  }, []);

  return (
    <Container className="chart-toast">
      {toasts.map((toast) => (
        <ToastItem key={toast._id} toast={toast} onRemove={removeToast} />
      ))}
    </Container>
  );
};

const ToastItem = ({
  toast,
  onRemove
}: {
  toast: ChartToastMessage;
  onRemove: (_id: string) => void;
}) => {
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        onRemove(toast._id);
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast, onRemove]);

  return (
    <Toast className={toast.type} onClick={() => onRemove(toast._id)}>
      <Message>{toast.message}</Message>
    </Toast>
  );
};

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
`;

const Toast = styled.div`
  pointer-events: auto;
  border-radius: 4px;
  padding: 10px 16px;
  box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.1), 0 2px 15px 0 rgba(0, 0, 0, 0.05);
  animation: ${slideIn} 0.3s ease-in-out;
  cursor: pointer;
  font-size: 14px;
  font-family: var(--fontSans);
  max-width: 300px;
  min-height: 42px;
  display: flex;
  align-items: center;
  word-break: break-word;
  color: #fff;

  &.info {
    background-color: #ffbd00;
  }

  &.success {
    background-color: var(--color-primary);
  }

  &.warning {
    background-color: #ffbd00;
  }

  &.error {
    background-color: var(--color-danger);
  }
`;

const Message = styled.div`
  color: inherit;
`;

export default ChartToast;
